/**
 * Rename a codename family via PATCH /codenames/{family_key}.
 *
 * Backend semantics mapped to a discriminated result so callers can
 * branch without sniffing axios errors themselves:
 * - 409 → 'conflict' (name already in use — tell the analyst)
 * - 404 → 'stale' (family renamed under our feet — refetch silently)
 * - anything else → 'error' (captured to Sentry)
 */

import { useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';

import { vnextApiClient } from '@/services/vnext-api';
import { notifyBackofficeProjectsBehind } from '@/utils/backofficeSyncNotice';

export type RenameCodenameResult =
  | { ok: true; familyKey: string; backofficeProjectsFailed: number }
  | { ok: false; reason: 'conflict' | 'stale' | 'error'; message: string };

interface UseRenameCodenameResult {
  rename: (familyKey: string, newCodename: string) => Promise<RenameCodenameResult>;
  isSaving: boolean;
}

export function useRenameCodename(): UseRenameCodenameResult {
  const [isSaving, setIsSaving] = useState(false);

  const rename = useCallback(
    async (familyKey: string, newCodename: string): Promise<RenameCodenameResult> => {
      setIsSaving(true);
      try {
        const result = await vnextApiClient.renameCodename(familyKey, newCodename);
        notifyBackofficeProjectsBehind(result.backofficeProjectsFailed);
        return {
          ok: true,
          familyKey: result.newCodename,
          backofficeProjectsFailed: result.backofficeProjectsFailed,
        };
      } catch (err) {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          const detail =
            (err.response?.data as { detail?: string } | undefined)?.detail ?? err.message;
          if (status === 409) {
            return { ok: false, reason: 'conflict', message: detail };
          }
          if (status === 404) {
            return { ok: false, reason: 'stale', message: detail };
          }
          if (status === 400 || status === 422) {
            return { ok: false, reason: 'error', message: detail };
          }
        }
        Sentry.captureException(err);
        return {
          ok: false,
          reason: 'error',
          message: err instanceof Error ? err.message : 'Failed to rename codename',
        };
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  return { rename, isSaving };
}
